//admindashboard.js
import React from "react";
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/oiiko loading.json";
import { useSubscription } from "../hooks/useSubscription";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { signOut } from "firebase/auth";
import WelcomeTour from "../components/WelcomeTour";
import {
  getWelcomeSeen,
  setWelcomeSeen,
  getPilotTourSeen,
  setPilotTourSeen,
} from "../utils/onboardingState";
import { db, auth } from "../firebase/config";
import { isPilotActive } from "../utils/pilot";

import {
  doc,
  getDoc,
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  addDoc, // 👈 for reviews
  updateDoc, // 👈 for reviewPrompt flags
} from "firebase/firestore";
import { DateTime } from "luxon";

import "../styles/AdminDashboard.css";
import NotificationModal from "../components/NotificationModal";
import { toast } from "react-toastify"; // 👈 to show review toasts

// Lucide icons
import {
  Users,
  Clock4,
  CalendarDays,
  Luggage,
  ReceiptText,
  GraduationCap,
  BarChart2,
  Headset,
  HelpCircle,
  Settings,
  CreditCard,
  LayoutDashboard, // ✅ ADD
  LogOut,
  Bell,
  UserRound,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  WalletCards,
  Search, // ✅ NEW
  Menu, // ✅ ADD
  X, // ✅ ADD
  Sparkles,
  CheckSquare, // clean, minimal task vibe
  Shield,
  Code2,
} from "lucide-react";

// Pages
import ManageEmployees from "./ManageEmployees";
import EmployerProfile from "./EmployerProfile";
import AttendanceTracking from "./EmployerTimeTracking";
import LeaveManagement from "./EmployerLeave";
import SchedulingEmployer from "./SchedulingEmployer";
import SubmitTickets from "./SubmitTicket";
import EmployerTraining from "./EmployerTraining";
import DashboardHome from "./DashboardHome";
import EmployeeDetails from "./EmployeeDetails";
import TaskManager from "./TaskManager";
import Analytics from "./Analytics";
import SkeletonLoader from "../components/SkeletonLoader";
import EmployerExpense from "./EmployerExpense";
import Oiiko from "../tools/oiikotranslogo2.png";
import Oiiko2 from "../tools/oiikotranstxt.png";
import Help from "./Help";
import QuickGuide from "./QuickGuide";
import PayrollReport from "./PayrollReport"; // 👈 ADD THIS
import GlobalSearchPalette from "../components/GlobalSearchPalette";

export default function AdminDashboard() {
  const routeTabLockRef = useRef(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const { subscription, loading: subLoading } = useSubscription();
  // state
  const [isQuickComplete, setIsQuickComplete] = useState(false);

  const { user, loading: authLoading } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [pilotActive, setPilotActive] = useState(false);
  const [pilotEndsDate, setPilotEndsDate] = useState(null); // string like "Dec 16"
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [hasNewWhatsNew, setHasNewWhatsNew] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isDevUser, setIsDevUser] = useState(false);
  const [effectiveSubscription, setEffectiveSubscription] = useState(null);

  const navigate = useNavigate();

  // 👇 settings dropdown state
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // Sidebar open/close with persistence
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  useEffect(() => {
    const saved = localStorage.getItem("admin.sidebarOpen");
    if (saved != null) setIsSidebarOpen(saved === "1");
  }, []);
  useEffect(() => {
    localStorage.setItem("admin.sidebarOpen", isSidebarOpen ? "1" : "0");
  }, [isSidebarOpen]);

  const [pageLoading, setPageLoading] = useState(false);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [companyName, setCompanyName] = useState("");
  const [, setCurrentTime] = useState("");

  const [isClearing, setIsClearing] = useState(false);
  const [enabledTypes, setEnabledTypes] = useState([]);

  // ⭐ In-app review state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(0); // 1–5
  const [reviewText, setReviewText] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);

  // ✅ Mobile drawer (hamburger sidebar)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const openMobileNav = () => setMobileNavOpen(true);
  const closeMobileNav = () => setMobileNavOpen(false);

  // ✅ Lock body scroll when mobile nav is open
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    const requested = location.state?.tab;
    if (requested) {
      routeTabLockRef.current = requested;
      setActiveTab(requested);
      navigate(".", { replace: true, state: {} });
    } else {
      const saved = localStorage.getItem("admin.activeTab");
      if (saved) setActiveTab(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (activeTab) localStorage.setItem("admin.activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (!user?.uid) return;
    const unSub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      const data = snap.data() || {};
      // ✅ NEW: platform admin flag
      setIsPlatformAdmin(data.isPlatformAdmin === true);

      const enabled = !!data.notificationsEnabled;
      const prefs = data.notificationPrefs || {};

      if (!enabled) {
        setEnabledTypes([]); // no types enabled when master switch is off
        return;
      }

      const types = Object.entries(prefs)
        .filter(([, v]) => v)
        .map(([k]) => k);

      setEnabledTypes(types);
    });
    return () => unSub();
  }, [user?.uid]);

  useEffect(() => {
    const justRegistered = localStorage.getItem("justRegistered");
    if (justRegistered === "true") {
      localStorage.removeItem("justRegistered");
      window.location.reload();
      return;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      if (authLoading || subLoading || !user?.uid) return;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      const role = userSnap.data()?.role || "employer";
      const companyId = userSnap.data()?.companyId;

      if (role === "employee") {
        navigate("/login");
        return;
      }

      if (!companyId) {
        navigate("/company-setup");
        return;
      }

      // ✅ Fetch company once
      const companyRef = doc(db, "companies", companyId);
      const companySnap = await getDoc(companyRef);
      const companyData = companySnap.data() || {};

      // ✅ Pilot overrides subscription gating
      const pilot = companyData.pilot;
      const activePilot = isPilotActive(pilot);

      if (!cancelled) setPilotActive(activePilot);
      // ✅ Store Pilot end date for top banner
      let endsDateStr = null;
      try {
        const ends =
          typeof pilot?.endsAt?.toDate === "function"
            ? pilot.endsAt.toDate()
            : typeof pilot?.endsAt?.seconds === "number"
            ? new Date(pilot.endsAt.seconds * 1000)
            : null;

        if (ends) {
          endsDateStr = DateTime.fromJSDate(ends)
            .setZone(companyData.timeZone || "UTC")
            .toFormat("MMMM dd");
        }
      } catch {}

      if (!cancelled) setPilotEndsDate(endsDateStr);

      // ✅ Only enforce subscription if NOT pilot
      // after you fetched companyData
      const effectiveSub = companyData.subscription || subscription || null;

      if (!cancelled) {
        setEffectiveSubscription(effectiveSub);
      }

      if (!activePilot) {
        // No plan has been selected yet
        if (!effectiveSub) {
          navigate("/subscription", { replace: true });
          return;
        }

        const status = String(effectiveSub.status || "").toLowerCase();

        if (!["free", "active", "trialing"].includes(status)) {
          navigate("/subscription", { replace: true });
          return;
        }
      }

      // ✅ Existing logic continues for BOTH pilot + non-pilot...
      const progress = companyData.quickSetupProgress || {};
      const plan = effectiveSub?.planName || companyData.plan || "Free";
      const REQUIRED_BY_PLAN = {
        Free: ["addedEmployee", "setNotifications"],
        Basic: ["addedEmployee", "setNotifications"],
        Standard: ["addedEmployee", "setNotifications"],
        Premium: ["addedEmployee", "setNotifications"],
        Training: ["addedEmployee", "setNotifications"],
        Helpdesk: ["addedEmployee", "setNotifications"],
      };

      const required = REQUIRED_BY_PLAN[plan] || REQUIRED_BY_PLAN.Basic;
      const complete = required.every((k) => progress?.[k] === true);
      setIsQuickComplete(complete);

      try {
        const seen = activePilot
          ? await getPilotTourSeen(db, companyId)
          : await getWelcomeSeen(db, companyId);
        const justFromCheckout = sessionStorage.getItem("postCheckout") === "1";
        if (!seen || justFromCheckout) {
          setShowWelcome(true);
          sessionStorage.removeItem("postCheckout");
        }
      } catch {}
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, subLoading, subscription, navigate]);

  useEffect(() => {
    if (routeTabLockRef.current && activeTab === routeTabLockRef.current)
      routeTabLockRef.current = null;
  }, [activeTab]);
  // ⌘K / Ctrl+K opens global search
  useEffect(() => {
    const handler = (e) => {
      const key = e.key?.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === "k") {
        e.preventDefault();
        setShowGlobalSearch(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    let cancelled = false;

    (async () => {
      try {
        // dev allowlist doc id must match the auth uid
        const snap = await getDoc(doc(db, "devUsers", user.uid));
        if (!cancelled) setIsDevUser(snap.exists());
      } catch (e) {
        console.error("Dev allowlist check failed:", e);
        if (!cancelled) setIsDevUser(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.companyId) return;
    const notifRef = collection(
      db,
      `companies/${user.companyId}/notifications`,
    );
    const q = query(notifRef, orderBy("timestamp", "desc"), limit(100));
    const unSub = onSnapshot(q, (snap) => {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const notArchived = all.filter((n) => n.archived !== true);

      const filtered =
        enabledTypes.length === 0
          ? [] // if none enabled, show none
          : notArchived.filter((n) => enabledTypes.includes(n.type));
      setNotifications(filtered);
      const activity = filtered.filter((n) => n.type !== "platformUpdates");
      setHasNewNotifications(activity.some((n) => !n.read));
    });
    return () => unSub();
  }, [user?.companyId, enabledTypes]);

  const activityNotifications = notifications.filter(
    (n) => n.type !== "platformUpdates",
  );

  const whatsNewNotifications = notifications.filter(
    (n) => n.type === "platformUpdates",
  );

  useEffect(() => {
    setHasNewWhatsNew(whatsNewNotifications.some((n) => !n.read));
  }, [whatsNewNotifications]);

  const handleClearNotifications = async (idsToClear) => {
    if (!user?.companyId) return;
    const targets =
      Array.isArray(idsToClear) && idsToClear.length
        ? notifications.filter((n) => idsToClear.includes(n.id))
        : notifications;
    if (!targets.length) return;

    setIsClearing(true);
    try {
      const batch = writeBatch(db);
      targets.forEach((n) => {
        const ref = doc(db, `companies/${user.companyId}/notifications`, n.id);
        batch.update(ref, {
          archived: true,
          archivedAt: serverTimestamp(),
          archivedBy: user.uid,
          read: true,
        });
      });
      await batch.commit();
    } catch (e) {
      console.error("Failed to clear notifications", e);
    } finally {
      setIsClearing(false);
    }
  };

  // ⏰ Load company info, keep live clock, and decide if we should show review prompt
  useEffect(() => {
    if (!user?.companyId) return;
    let intervalId;
    (async () => {
      try {
        const companyRef = doc(db, "companies", user.companyId);
        const companyDoc = await getDoc(companyRef);
        const data = companyDoc.data() || {};

        const tz = data.timeZone || "UTC";
        const name = data.name || "";
        setCompanyTimeZone(tz);
        setCompanyName(name);

        // Live clock
        intervalId = setInterval(() => {
          const now = DateTime.now().setZone(tz);
          setCurrentTime(now.toFormat("cccc, dd LLL yyyy • hh:mm a"));
        }, 1000);

        // In-app review prompt logic
        const createdAt = data.createdAt; // Firestore Timestamp
        const reviewPrompt = data.reviewPrompt || {};
        const REVIEW_DELAY_DAYS = 3;

        if (createdAt && !reviewPrompt.completed && !reviewPrompt.dismissed) {
          const createdSeconds =
            createdAt.seconds || createdAt._seconds || createdAt;
          const created = DateTime.fromSeconds(createdSeconds).setZone(tz);
          const now = DateTime.now().setZone(tz);
          const diffDays = now.diff(created, "days").days;

          if (diffDays >= REVIEW_DELAY_DAYS) {
            setShowReviewModal(true);
          }
        }
      } catch (e) {
        console.error("Failed to load company/timezone/review data", e);
      }
    })();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.companyId]);

  const handleTabChange = (tab) => {
    setPageLoading(true);
    setTimeout(() => {
      setActiveTab(tab);
      setPageLoading(false);
      setMobileNavOpen(false); // ✅ close mobile drawer on navigation
    }, 400);
  };

  useEffect(() => {
    if (!showNotifications || !user?.companyId) return;
    const unread = activityNotifications.filter((n) => !n.read);
    if (!unread.length) return;

    const batch = writeBatch(db);
    unread.forEach((n) => {
      const ref = doc(db, `companies/${user.companyId}/notifications`, n.id);
      batch.update(ref, { read: true });
    });

    batch
      .commit()
      .then(() => setHasNewNotifications(false))
      .catch(() => {});
  }, [showNotifications, user?.companyId, activityNotifications]);

  useEffect(() => {
    if (!showWhatsNew || !user?.companyId) return;
    const unread = whatsNewNotifications.filter((n) => !n.read);
    if (!unread.length) return;

    const batch = writeBatch(db);
    unread.forEach((n) => {
      const ref = doc(db, `companies/${user.companyId}/notifications`, n.id);
      batch.update(ref, { read: true });
    });

    batch
      .commit()
      .then(() => setHasNewWhatsNew(false))
      .catch(() => {});
  }, [showWhatsNew, user?.companyId, whatsNewNotifications]);

  const planName = pilotActive
    ? "Premium"
    : effectiveSubscription?.planName || subscription?.planName || "Free";
  // ✅ Hide Dashboard tab for standalone plans
  const hideDashboardTab = planName === "Training" || planName === "Helpdesk";

  // ✅ Only show "Upgrade now" for lower plans (hide for Premium / Pilot)
  const LOWER_PLANS = ["Free", "Basic", "Standard", "Training", "Helpdesk"];
  const showUpgradeNow = !pilotActive && LOWER_PLANS.includes(planName);

  const featureAccess = {
    Free: [
      "Employer Profile",
      "Employee Management",
      "Attendance & Time Tracking",
      "Scheduling",
      "Payroll",
    ],
    Basic: [
      "Employer Profile",
      "Employee Management",
      "Attendance & Time Tracking",
      "Scheduling",
      "Payroll",
    ],
    Standard: [
      "Employer Profile",
      "Employee Management",
      "Attendance & Time Tracking",
      "Scheduling",
      "Payroll",
      "Leave & Absence Management",
      "Expense Management",
      "Task Manager", // ✅ ADD
    ],
    Premium: [
      "Employer Profile",
      "Employee Management",
      "Attendance & Time Tracking",
      "Leave & Absence Management",
      "Reports & Analytics",
      "Scheduling",
      "Training",
      "Tickets",
      "Expense Management",
      "Payroll",
      "Task Manager", // ✅ ADD
    ],
    Training: [
      "Employer Profile",
      "Employee Management",
      "Training",
      "Reports & Analytics",
    ],
    Helpdesk: ["Employer Profile", "Employee Management", "Tickets"],
  };

  // Map tabs -> the labels used in featureAccess
  const FEATURE_MAP = {
    "employee-management": "Employee Management",
    attendance: "Attendance & Time Tracking",
    Scheduling: "Scheduling",
    leave: "Leave & Absence Management",
    Expense: "Expense Management",
    Training: "Training",
    analytics: "Reports & Analytics",
    Tickets: "Tickets",
    Payroll: "Payroll",
    taskmanager: "Task Manager", // ✅ ADD
    // help / quick-guide / settings / subscription are always allowed
  };

  const allowedList = featureAccess[planName] || [];
  const hasFeature = (featureName) => allowedList.includes(featureName);

  // A tab is allowed if it's not mapped (e.g., "help") or its feature is in plan
  const tabAllowed = (tab) => !FEATURE_MAP[tab] || hasFeature(FEATURE_MAP[tab]);

  // Pages available to the global search palette
  const NAV_SEARCH_ITEMS = React.useMemo(() => {
    const items = [];
    if (!hideDashboardTab) {
      items.push({
        id: "nav-dashboard",
        type: "tab",
        tab: "dashboard",
        label: "Dashboard",
        description: "Overview of your workforce, alerts, and quick actions",
        keywords: "dashboard overview home summary",
      });
    }

    if (tabAllowed("employee-management")) {
      items.push({
        id: "nav-employees",
        type: "tab",
        tab: "employee-management",
        label: "Employees",
        description: "Manage your team and employee records",
        keywords: "employees staff team people directory",
      });
    }

    if (tabAllowed("attendance")) {
      items.push({
        id: "nav-time-tracking",
        type: "tab",
        tab: "attendance",
        label: "Time tracking",
        description: "Review and edit clock-ins, clock-outs, and hours",
        keywords: "time tracking attendance timesheets",
      });
    }

    if (tabAllowed("Scheduling")) {
      items.push({
        id: "nav-scheduling",
        type: "tab",
        tab: "Scheduling",
        label: "Scheduling",
        description: "Create weekly schedules and manage shifts",
        keywords: "schedule rota shifts planning",
      });
    }

    if (tabAllowed("leave")) {
      items.push({
        id: "nav-leave",
        type: "tab",
        tab: "leave",
        label: "Leave Management",
        description: "Review and approve time off requests",
        keywords: "leave vacation time off absence",
      });
    }

    if (tabAllowed("Expense")) {
      items.push({
        id: "nav-expenses",
        type: "tab",
        tab: "Expense",
        label: "Expense Management",
        description: "Approve expenses and monitor budgets",
        keywords: "expenses receipts reimbursements",
      });
    }

    if (tabAllowed("Training")) {
      items.push({
        id: "nav-training",
        type: "tab",
        tab: "Training",
        label: "Training Manager",
        description: "Assign and track employee training",
        keywords: "training courses learning",
      });
    }

    if (tabAllowed("analytics")) {
      items.push({
        id: "nav-analytics",
        type: "tab",
        tab: "analytics",
        label: "Reports & Analytics",
        description: "View analytics across employees, time, and leave",
        keywords: "reports analytics dashboards insights",
      });
    }

    if (tabAllowed("Payroll")) {
      items.push({
        id: "nav-payroll",
        type: "tab",
        tab: "Payroll",
        label: "Payroll",
        description: "Generate payroll reports and CSV exports",
        keywords: "payroll pay overtime",
      });
    }

    if (tabAllowed("Tickets")) {
      items.push({
        id: "nav-helpdesk",
        type: "tab",
        tab: "Tickets",
        label: "IT Helpdesk",
        description: "Manage IT tickets and SLAs",
        keywords: "tickets helpdesk support sla",
      });
    }

    if (tabAllowed("taskmanager")) {
      items.push({
        id: "nav-task-manager",
        type: "tab",
        tab: "taskmanager",
        label: "Task Manager",
        description: "Assign, track, and manage employee tasks",
        keywords: "tasks task manager assignments to-dos todo checklist",
      });
    }

    // Help Center inside dashboard
    items.push({
      id: "nav-help-center",
      type: "tab",
      tab: "help",
      label: "Help Center",
      description: "Browse guides for each module",
      keywords: "help support docs guides",
    });

    // Settings & subscription live on their own routes
    items.push({
      id: "nav-settings",
      type: "route",
      path: "/Settings",
      label: "Settings & Billing",
      description:
        "Account security, language, time zone, notifications, billing",
      keywords: "settings billing account language timezone",
    });

    items.push({
      id: "nav-subscription",
      type: "route",
      path: "/subscription",
      label: "Subscription",
      description: "Manage your Oiiko subscription plan",
      keywords: "subscription plan upgrade downgrade pricing",
    });

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planName, allowedList, isQuickComplete]);

  const getDefaultTab = () => {
    if (planName === "Helpdesk" && hasFeature("Tickets")) return "Tickets";
    if (hasFeature("Employee Management")) return "employee-management";

    const first = Object.entries(FEATURE_MAP).find(([, feat]) =>
      hasFeature(feat),
    );

    const fallback = first ? first[0] : "help";

    // ✅ never send standalone plans to dashboard
    if (hideDashboardTab && fallback === "dashboard") return "help";

    return fallback;
  };
  // ✅ Ensure active tab is valid for plan
  useEffect(() => {
    // ✅ If Dashboard is hidden for this plan, never allow it as active
    if (hideDashboardTab && activeTab === "dashboard") {
      const safe = getDefaultTab();
      setActiveTab(safe);
      localStorage.setItem("admin.activeTab", safe);
      return;
    }

    if (!tabAllowed(activeTab)) {
      const safe = getDefaultTab();
      setActiveTab(safe);
      localStorage.setItem("admin.activeTab", safe);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planName, isQuickComplete, hideDashboardTab]);

  if (subLoading) {
    return (
      <div className="admin-loading-screen">
        <Lottie
          animationData={loadingAnimation}
          loop
          autoplay
          className="admin-loading-lottie"
        />
        <p className="admin-loading-text">Loading your dashboard…</p>
      </div>
    );
  }

  const renderTabContent = () => {
    if (pageLoading) return <SkeletonLoader />;
    if (activeTab.startsWith("employee-details:")) {
      const employeeId = activeTab.split(":")[1];
      return (
        <EmployeeDetails
          employeeId={employeeId}
          goBack={() => setActiveTab("employee-management")}
        />
      );
    }
    switch (activeTab) {
      case "dashboard":
        return <DashboardHome onTabChange={handleTabChange} />;
      case "employer-profile":
        return <EmployerProfile />;
      case "employee-management":
        return (
          <ManageEmployees
            onEmployeeClick={(id) => setActiveTab(`employee-details:${id}`)}
          />
        );
      case "quick-guide":
        return <QuickGuide onTabChange={handleTabChange} />;
      case "attendance":
        return <AttendanceTracking />;
      case "leave":
        return <LeaveManagement />;
      case "Expense":
        return <EmployerExpense />;
      case "Scheduling":
        return <SchedulingEmployer />;
      case "Training":
        return <EmployerTraining />;
      case "Tickets":
        return <SubmitTickets />;
      case "taskmanager":
        return <TaskManager />;
      case "analytics":
        return <Analytics allowedFeatures={featureAccess[planName] || []} />;
      case "Payroll": // 👈 ADD THIS
        return <PayrollReport />;
      case "help":
        return <Help onTabChange={handleTabChange} />;

      default:
        return (
          <ManageEmployees
            onEmployeeClick={(id) => setActiveTab(`employee-details:${id}`)}
          />
        );
    }
  };

  const TAB_LABELS = {
    dashboard: "Dashboard",
    "employee-management": "Employees",
    attendance: "Time tracking",
    Scheduling: "Scheduling",
    leave: "Leave Management",
    Expense: "Expense Management",
    Training: "Training Manager",
    analytics: "Analytics",
    Payroll: "Payroll",
    Tickets: "IT helpdesk",
    taskmanager: "Task Manager",
    help: "Help Center",
    "quick-guide": "Quick setup",
  };

  const pageTitle = activeTab?.startsWith("employee-details:")
    ? "Employee Details"
    : TAB_LABELS[activeTab] || "Dashboard";

  const subStatus = subscription?.status;
  const nowSec = Math.floor(Date.now() / 1000);

  // ✅ normalize Firestore Timestamp / Date / number / string -> seconds
  const toSeconds = (v) => {
    if (!v) return null;

    // Firestore Timestamp
    if (typeof v?.toDate === "function") {
      return Math.floor(v.toDate().getTime() / 1000);
    }
    if (typeof v?.seconds === "number") return v.seconds;

    // JS Date
    if (v instanceof Date) return Math.floor(v.getTime() / 1000);

    // number or numeric string
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const trialEndSec = toSeconds(
    subscription?.trial_end ?? subscription?.trialEnd,
  );

  // (optional fallback if your hook only provides `created`)
  const createdSec = toSeconds(subscription?.created);
  const rawTrialEndSec =
    trialEndSec ?? (createdSec ? createdSec + 86400 : null);

  const isTrialing =
    subStatus === "trialing" && rawTrialEndSec && nowSec < rawTrialEndSec;

  const isActive =
    subStatus === "active" ||
    (subStatus === "trialing" && rawTrialEndSec && nowSec >= rawTrialEndSec);

  // ✅ format in company timezone (uses companyTimeZone state you already have)
  const trialEndsDate = rawTrialEndSec
    ? DateTime.fromSeconds(rawTrialEndSec)
        .setZone(companyTimeZone)
        .toFormat("MMMM dd")
    : null;

  console.log("[AdminDashboard Trial Debug]", {
    subLoading,
    subStatus,
    subscription,
    trial_end: subscription?.trial_end,
    trialEnd: subscription?.trialEnd,
    created: subscription?.created,
    nowSec,
    trialEndSec,
    rawTrialEndSec,
    isTrialing,
    isActive,
    trialEndsDate,
    companyTimeZone,
    pilotActive,
  });

  const NavItem = ({ id, icon: Icon, label, active, onClick, danger }) => (
    <button
      id={id}
      className={`side-item ${active ? "active" : ""} ${
        danger ? "danger" : ""
      }`}
      onClick={onClick}
      type="button"
    >
      <Icon className="side-ico" />
      <span className="side-label">{label}</span>
    </button>
  );

  // ⭐ Submit in-app review
  const handleReviewSubmit = async () => {
    if (!user?.uid || !user?.companyId) return;

    if (!reviewRating) {
      toast.error("Please select a rating before submitting.");
      return;
    }

    setReviewSaving(true);
    try {
      const payload = {
        context: "in_app_review",
        rating: reviewRating,
        feedbackText: reviewText.trim(),
        userId: user.uid,
        companyId: user.companyId,
        companyName: companyName || null,
        createdAt: serverTimestamp(),
      };

      // 1) Company-scoped reviews subcollection
      const companyReviewsRef = collection(
        db,
        "companies",
        user.companyId,
        "reviews",
      );
      await addDoc(companyReviewsRef, payload);

      // 2) Global reviews collection
      const globalReviewsRef = collection(db, "reviews");
      await addDoc(globalReviewsRef, payload);

      // 3) Mark prompt as completed so we don't ask again
      const companyRef = doc(db, "companies", user.companyId);
      await updateDoc(companyRef, {
        "reviewPrompt.completed": true,
        "reviewPrompt.completedAt": serverTimestamp(),
        "reviewPrompt.rating": reviewRating,
      });

      toast.success("Thanks for your review!");
      setShowReviewModal(false);
    } catch (e) {
      console.error("Failed to submit in-app review", e);
      toast.error("Could not save your review. Please try again later.");
      setShowReviewModal(false);
    } finally {
      setReviewSaving(false);
    }
  };

  // 🙅 Dismiss in-app review
  const handleReviewDismiss = async () => {
    setShowReviewModal(false);
    try {
      if (user?.companyId) {
        const companyRef = doc(db, "companies", user.companyId);
        await updateDoc(companyRef, {
          "reviewPrompt.dismissed": true,
          "reviewPrompt.lastAskedAt": serverTimestamp(),
        });
      }
    } catch (e) {
      console.error("Failed to mark review prompt dismissed", e);
    }
  };

  return (
    <div
      className={`admin-container ${isSidebarOpen ? "sb-open" : "sb-closed"}`}
    >
      {/* Top bar (stays on top) */}

      <nav className="admin-navbar">
        <div className="nav-left">
          <button
            className="nav-icon-button nav-hamburger"
            aria-label="Open menu"
            onClick={openMobileNav}
            type="button"
          >
            <Menu className="nav-icon" />
          </button>
          <div className="nav-page-title">{pageTitle}</div>
        </div>

        <div className="nav-right">
          {/* ✅ (Temporarily hidden) Pilot / Trial / Active plan banners */}
          {/*
  {pilotActive ? (
    <div className="pilot-banner1">
      Pilot Program · Ends {pilotEndsDate || "Soon"}
    </div>
  ) : isTrialing ? (
    trialEndsDate && (
      <div className="trial-banner">
        Free Trial · Ends {trialEndsDate}
      </div>
    )
  ) : isActive ? (
    <div className="activeplan-banner">{planName}</div>
  ) : null}
  */}

          <div className="upgrade-now-btn1">
            {/* ✅ Upgrade button for lower plans only */}
            {showUpgradeNow && (
              <button
                className="upgrade-now-btn2"
                type="button"
                onClick={() =>
                  navigate("/subscription", {
                    state: { intent: "upgrade", from: "topnav_upgrade_button" },
                  })
                }
              >
                Upgrade now
              </button>
            )}
          </div>
          {/* 🔍 Global search */}
          <button
            id="nav-search-btn"
            className="nav-icon-button"
            title="Search (⌘K / Ctrl+K)"
            aria-label="Search"
            onClick={() => setShowGlobalSearch(true)}
          >
            <Search className="nav-icon" />
          </button>

          <button
            id="nav-notifications-btn"
            className="nav-icon-button"
            title="Notifications"
            onClick={() => setShowNotifications(true)}
            aria-label="Notifications"
          >
            <Bell className="nav-icon" />
            {hasNewNotifications && <span className="notification-dot" />}
          </button>
          <button
            id="nav-whatsnew-btn"
            className="nav-icon-button"
            title="What’s New"
            onClick={() => setShowWhatsNew(true)}
            aria-label="What’s New"
          >
            <Sparkles className="nav-icon" />
            {hasNewWhatsNew && <span className="notification-dot" />}
          </button>

          {/* Settings + Subscription dropdown in top nav */}
          <div className="profile-wrapper">
            <button
              id="nav-profile-btn"
              className="nav-icon-button profile-button"
              title="Account"
              aria-label="Account"
              onClick={() => setShowSettingsMenu((open) => !open)}
            >
              <UserRound className="nav-icon" />
              <span className="chevron">{showSettingsMenu}</span>
            </button>

            {showSettingsMenu && (
              <div className="profile-menu">
                {/* Account Profile */}
                <button
                  className="menu-item"
                  type="button"
                  onClick={() => {
                    setShowSettingsMenu(false);
                    navigate("/employer-profile");
                  }}
                >
                  <UserRound className="nav-ico" />
                  <span>Account Profile</span>
                </button>

                {/* Divider */}
                <div className="menu-divider" />

                {/* Settings */}
                <button
                  className="menu-item"
                  type="button"
                  onClick={() => {
                    setShowSettingsMenu(false);
                    navigate("/settings");
                  }}
                >
                  <Settings className="nav-ico" />
                  <span>Settings</span>
                </button>

                {/* Subscription */}
                <button
                  className="menu-item"
                  type="button"
                  onClick={() => {
                    setShowSettingsMenu(false);
                    navigate("/subscription", {
                      state: { intent: "upgrade", from: "topnav" },
                    });
                  }}
                >
                  <CreditCard className="nav-ico" />
                  <span>Subscription</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
      {/* ✅ Mobile nav overlay + drawer */}
      {mobileNavOpen && (
        <div
          className="mobile-nav-backdrop"
          onClick={closeMobileNav}
          role="button"
          aria-label="Close menu"
          tabIndex={0}
        />
      )}

      <aside className={`mobile-sidenav ${mobileNavOpen ? "open" : ""}`}>
        <div className="mobile-sidenav-header">
          <div className="mobile-sidenav-brand">
            <img src={Oiiko} alt="Oiiko" className="brand-logo" />
            <img src={Oiiko2} alt="Oiiko" className="brand-logo1" />
          </div>

          <button
            className="nav-icon-button"
            aria-label="Close menu"
            onClick={closeMobileNav}
            type="button"
          >
            <X className="nav-icon" />
          </button>
        </div>

        <div className="mobile-sidenav-body">
          {!isQuickComplete && (
            <NavItem
              icon={ClipboardList}
              label="Quick setup"
              active={activeTab === "quick-guide"}
              onClick={() => handleTabChange("quick-guide")}
            />
          )}

          {!hideDashboardTab && (
            <NavItem
              icon={LayoutDashboard}
              label="Dashboard"
              active={activeTab === "dashboard"}
              onClick={() => handleTabChange("dashboard")}
            />
          )}

          {tabAllowed("employee-management") && (
            <NavItem
              icon={Users}
              label="Employees"
              active={activeTab === "employee-management"}
              onClick={() => handleTabChange("employee-management")}
            />
          )}

          {tabAllowed("attendance") && (
            <NavItem
              icon={Clock4}
              label="Time tracking"
              active={activeTab === "attendance"}
              onClick={() => handleTabChange("attendance")}
            />
          )}

          {tabAllowed("Scheduling") && (
            <NavItem
              icon={CalendarDays}
              label="Scheduling"
              active={activeTab === "Scheduling"}
              onClick={() => handleTabChange("Scheduling")}
            />
          )}
          {tabAllowed("Payroll") && (
            <NavItem
              icon={WalletCards}
              label="Payroll"
              active={activeTab === "Payroll"}
              onClick={() => handleTabChange("Payroll")}
            />
          )}
          {tabAllowed("leave") && (
            <NavItem
              icon={Luggage}
              label="Leave Management"
              active={activeTab === "leave"}
              onClick={() => handleTabChange("leave")}
            />
          )}

          {tabAllowed("Expense") && (
            <NavItem
              icon={ReceiptText}
              label="Expense Management"
              active={activeTab === "Expense"}
              onClick={() => handleTabChange("Expense")}
            />
          )}

          {tabAllowed("taskmanager") && (
            <NavItem
              icon={CheckSquare}
              label="Task Manager"
              active={activeTab === "taskmanager"}
              onClick={() => handleTabChange("taskmanager")}
            />
          )}

          {tabAllowed("Training") && (
            <NavItem
              icon={GraduationCap}
              label="Training Manager"
              active={activeTab === "Training"}
              onClick={() => handleTabChange("Training")}
            />
          )}
          {tabAllowed("Tickets") && (
            <NavItem
              icon={Headset}
              label="IT helpdesk"
              active={activeTab === "Tickets"}
              onClick={() => handleTabChange("Tickets")}
            />
          )}

          {tabAllowed("analytics") && (
            <NavItem
              icon={BarChart2}
              label="Reports & Analytics"
              active={activeTab === "analytics"}
              onClick={() => handleTabChange("analytics")}
            />
          )}

          <NavItem
            icon={HelpCircle}
            label="Help center"
            active={activeTab === "help"}
            onClick={() => handleTabChange("help")}
            id="nav-help-center-mobile"
          />
          {isPlatformAdmin && (
            <NavItem
              icon={Shield}
              label="Platform Inbox"
              active={false}
              onClick={() => {
                closeMobileNav();
                navigate("/platform-inbox");
              }}
            />
          )}
          {isDevUser && (
            <NavItem
              icon={Code2}
              label="Dev Console"
              active={false}
              onClick={() => {
                closeMobileNav();
                navigate("/dev/announce");
              }}
              id="nav-dev-console-mobile"
            />
          )}
        </div>

        <div className="mobile-sidenav-footer">
          <button
            className="side-item danger"
            onClick={async () => {
              try {
                await signOut(auth);
                closeMobileNav();
                navigate("/login");
              } catch {}
            }}
            type="button"
          >
            <LogOut className="side-ico" />
            <span className="side-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Sidebar (now spans full height; underlaps the top bar) */}
      <aside className={`sidenav ${isSidebarOpen ? "open" : "closed"}`}>
        <div className="brand">
          <img src={Oiiko} alt="Oiiko" className="brand-logo" />
          {isSidebarOpen && (
            <img src={Oiiko2} alt="Oiiko" className="brand-logo1" />
          )}
          <button
            id="sidebar-toggle-btn"
            className="side-collapse"
            aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            onClick={() =>
              setIsSidebarOpen((open) => {
                const next = !open;
                localStorage.setItem("admin.sidebarOpen", next ? "1" : "0");
                return next;
              })
            }
          >
            {isSidebarOpen ? (
              <ChevronLeft
                size={16}
                strokeWidth={3.25}
                className="collapse-icon"
              />
            ) : (
              <ChevronRight
                size={16}
                strokeWidth={3.25}
                className="collapse-icon"
              />
            )}
          </button>
        </div>

        <div className="sidenav-top">
          {!isQuickComplete && (
            <NavItem
              icon={ClipboardList}
              label="Quick setup"
              active={activeTab === "quick-guide"}
              onClick={() => handleTabChange("quick-guide")}
            />
          )}
          {!hideDashboardTab && (
            <NavItem
              icon={LayoutDashboard}
              label="Dashboard"
              active={activeTab === "dashboard"}
              onClick={() => handleTabChange("dashboard")}
            />
          )}

          {tabAllowed("employee-management") && (
            <NavItem
              icon={Users}
              label="Employees"
              active={activeTab === "employee-management"}
              onClick={() => handleTabChange("employee-management")}
            />
          )}

          {tabAllowed("attendance") && (
            <NavItem
              icon={Clock4}
              label="Time tracking"
              active={activeTab === "attendance"}
              onClick={() => handleTabChange("attendance")}
            />
          )}

          {tabAllowed("Scheduling") && (
            <NavItem
              icon={CalendarDays}
              label="Scheduling"
              active={activeTab === "Scheduling"}
              onClick={() => handleTabChange("Scheduling")}
            />
          )}

          {tabAllowed("Payroll") && (
            <NavItem
              icon={WalletCards}
              label="Payroll"
              active={activeTab === "Payroll"}
              onClick={() => handleTabChange("Payroll")}
            />
          )}

          {tabAllowed("leave") && (
            <NavItem
              icon={Luggage}
              label="Leave Management"
              active={activeTab === "leave"}
              onClick={() => handleTabChange("leave")}
            />
          )}

          {tabAllowed("Expense") && (
            <NavItem
              icon={ReceiptText}
              label="Expense Management"
              active={activeTab === "Expense"}
              onClick={() => handleTabChange("Expense")}
            />
          )}

          {tabAllowed("Training") && (
            <NavItem
              icon={GraduationCap}
              label="Training Manager"
              active={activeTab === "Training"}
              onClick={() => handleTabChange("Training")}
            />
          )}

          {tabAllowed("taskmanager") && (
            <NavItem
              icon={CheckSquare}
              label="Task Manager"
              active={activeTab === "taskmanager"}
              onClick={() => handleTabChange("taskmanager")}
            />
          )}
          {tabAllowed("Tickets") && (
            <NavItem
              icon={Headset}
              label="IT helpdesk"
              active={activeTab === "Tickets"}
              onClick={() => handleTabChange("Tickets")}
            />
          )}
          {tabAllowed("analytics") && (
            <NavItem
              icon={BarChart2}
              label="Reports & Analytics"
              active={activeTab === "analytics"}
              onClick={() => handleTabChange("analytics")}
            />
          )}

          <NavItem
            icon={HelpCircle}
            label="Help center"
            active={activeTab === "help"}
            onClick={() => handleTabChange("help")}
            id="nav-help-center"
          />
          {isPlatformAdmin && (
            <NavItem
              icon={Shield}
              label="Platform Inbox"
              active={location.pathname === "/platform-inbox"}
              onClick={() => navigate("/platform-inbox")}
            />
          )}
          {isDevUser && (
            <NavItem
              icon={Code2}
              label="Dev Console"
              active={false}
              onClick={() => navigate("/dev/announce")}
              id="nav-dev-console"
            />
          )}
        </div>

        <div className="sidenav-bottom">
          <button
            className="side-item danger"
            onClick={async () => {
              try {
                await signOut(auth);
                navigate("/login");
              } catch {}
            }}
            type="button"
          >
            <LogOut className="side-ico" />
            <span className="side-label">Logout</span>
          </button>
        </div>
      </aside>

      <div className="admin-main-content">{renderTabContent()}</div>
      {showNotifications && (
        <NotificationModal
          title="Notifications"
          mode="activity"
          notifications={activityNotifications}
          onClose={() => {
            setShowNotifications(false);
            setHasNewNotifications(false);
          }}
          onClear={handleClearNotifications}
          isClearing={isClearing}
          companyTimeZone={companyTimeZone}
        />
      )}

      {showWhatsNew && (
        <NotificationModal
          title="What’s New"
          mode="announcements"
          notifications={whatsNewNotifications}
          onClose={() => {
            setShowWhatsNew(false);
            setHasNewWhatsNew(false);
          }}
          onClear={handleClearNotifications}
          isClearing={isClearing}
          companyTimeZone={companyTimeZone}
        />
      )}

      <WelcomeTour
        open={showWelcome}
        companyName={companyName}
        planName={planName}
        onOpenSidebar={() => setIsSidebarOpen(true)}
        onJumpTab={(tab) => tab && handleTabChange(tab)}
        onClose={async () => {
          setShowWelcome(false);
          try {
            if (!user?.companyId) return;

            if (pilotActive) {
              await setPilotTourSeen(db, user.companyId);
            } else {
              await setWelcomeSeen(db, user.companyId);
            }
          } catch {}
        }}
      />

      {/* 🔍 Global search command palette */}
      {showGlobalSearch && (
        <GlobalSearchPalette
          open={showGlobalSearch}
          onClose={() => setShowGlobalSearch(false)}
          navItems={NAV_SEARCH_ITEMS}
          planName={planName}
          onTabChange={(tabKey) => handleTabChange(tabKey)}
        />
      )}

      {/* ⭐ In-app review prompt for new employers */}
      {showReviewModal && (
        <div className="cancel-modal-backdrop">
          <div className="cancel-modal review-modal">
            <h3>How is Oiiko working for you so far?</h3>
            <p style={{ marginTop: 8 }}>
              You’ve been using Oiiko for a few days. We’d love a quick rating
              so we can keep improving your experience.
            </p>

            <div
              className="review-stars"
              style={{
                marginTop: 14,
                marginBottom: 10,
                display: "flex",
                gap: 6,
                justifyContent: "center",
              }}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setReviewRating(n)}
                  className={`star-btn ${reviewRating >= n ? "selected" : ""}`}
                  style={{
                    border: "none",
                    background: "transparent",
                    fontSize: 24,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  {reviewRating >= n ? "★" : "☆"}
                </button>
              ))}
            </div>

            <label className="label" style={{ marginTop: 8 }}>
              Anything we can improve?
            </label>
            <textarea
              className="input"
              style={{ minHeight: 80, resize: "vertical" }}
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Optional, but very helpful..."
            />

            <div className="cancel-modal-actions" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="cancel-modal-secondary"
                onClick={handleReviewDismiss}
                disabled={reviewSaving}
              >
                Not now
              </button>
              <button
                type="button"
                className="general-savebuttons"
                onClick={handleReviewSubmit}
                disabled={reviewSaving}
              >
                {reviewSaving ? "Sending..." : "Send review"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
